<script setup lang="ts">
import {ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import MarkdownView from "@/core/components/markdown/Markdown.vue";
import ExtensionDetailsHeader from "@/extension/components/extension-details-header/ExtensionDetailsHeader.vue";
import ExtensionConfigurator from "@/extension/components/extension-configurators/ExtensionConfigurator.vue";
import ExtensionNavigationService from "@/extension/services/ExtensionNavigationService";
import ExtensionService from "@/extension/services/ExtensionService";
import Extension from "@/core/interfaces/extension/Extension";
import {extensionApi} from "@/container";
import {useExtensionStore} from "@/stores/extension.store";
import {useToastStore} from "@/stores/toast.store";

const props = defineProps<{
    /** The extension to show, from the route. */
    extensionId: string;
}>();

const router = useRouter();
const route = useRoute();
const extensionStore = useExtensionStore();
const {showSuccess, showError} = useToastStore();

const extension = ref<Extension | null>(null);
const loading = ref(true);
const toggling = ref(false);

/**
 * Read the extension.
 *
 * @param id     The extension id.
 * @param silent Whether to read it again without the spinner: the page stays
 *               mounted, and the configurator on it keeps where it stands.
 */
const load = async (id: number, silent = false): Promise<void> => {
    loading.value = !silent;

    try {
        extension.value = await extensionApi().read(id);
    } finally {
        loading.value = false;
    }
};

/**
 * Record that the pharmacy has been through the configuration, so the next
 * click on the module opens the module itself rather than this page — and the
 * notification popup shows the module's entry.
 *
 * Said on screen either way. The write is an update of the organization's
 * row for this module, and the API does nothing, without a word, when that
 * row is missing — disabling the module removes it, re-enabling recreates it
 * blank. A pharmacist who was told nothing spent an afternoon looking for a
 * popup entry that the API had never been told to show.
 *
 * @returns Whether the configuration was recorded: the configurator moves on
 *          only then.
 */
const configure = async (): Promise<boolean> => {
    if (!extension.value) {
        return false;
    }

    let recorded = false;

    try {
        const updated = await extensionApi().markConfigured(extension.value.id);

        recorded = !!updated.configured;

        if (recorded) {
            showSuccess({content: 'Configuration enregistrée : le module est prêt pour la pharmacie.'});
        } else {
            showError({content: 'La configuration n\'a pas été enregistrée. Vérifiez que le module est bien activé, puis terminez de nouveau la configuration.'});
        }
    } catch {
        showError({content: 'La configuration n\'a pas pu être enregistrée. Vérifiez la connexion, puis réessayez.'});
    }

    // Best-effort: a refresh that fails does not undo what the API recorded,
    // and the configurator moves on all the same.
    await Promise.allSettled([load(extension.value.id, true), extensionStore.refreshExtensions()]);

    return recorded;
};

/**
 * Turn the module on or off from the header.
 *
 * Buying is handed back to the store modal, which owns the checkout: this page
 * is where a module is set up, not where it is sold.
 */
const toggle = async (): Promise<void> => {
    if (!extension.value || toggling.value) {
        return;
    }

    const next = ExtensionService.getNextAction(extension.value);

    if (next.action === 'buy') {
        ExtensionNavigationService.showExtensionDetails(extension.value.id, router, route);

        return;
    }

    toggling.value = true;

    try {
        await (next.action === 'disable'
            ? extensionStore.disableExtension(extension.value.id)
            : extensionStore.enableExtension(extension.value.id));

        await load(extension.value.id);
    } finally {
        toggling.value = false;
    }
};

watch(() => props.extensionId, (extensionId) => load(Number(extensionId)), {immediate: true});
</script>

<template>
  <BaseContainer>
    <BaseFlexSpinner v-if="loading" />
    <div v-else-if="extension" class="flex flex-col gap-5 mb-10">
      <ExtensionDetailsHeader
        :extension="extension"
        :loading="toggling"
        @button-click="toggle"
      />
      <div class="p-6 bg-lightPurple-lightest">
        <MarkdownView :content="extension.description" />
      </div>
      <ExtensionConfigurator
        v-if="extension.configurator"
        :slug="extension.configurator"
        :extension="extension"
        :configure="configure"
        class="mx-6"
      />
    </div>
  </BaseContainer>
</template>
